import { useState } from 'react';
import {
  Banner,
  Button,
  DiffView,
  Markdown,
  Modal,
  MonoLabel,
  Skeleton,
  Tabs,
  Tag,
  TextAreaField,
  Toast
} from '../components/primitives';
import { SettingsBackLink } from '../components/settings/SettingsBackLink';
import {
  resolveStandardStatus,
  standardStatusLabel,
  STANDARDS_SOFT_WARN,
  STARTER_EXAMPLES,
  type OrgStandard,
  type OrgStandardVersion
} from '../lib/models/orgStandards';
import { useSettingsOrgStandardsVM } from '../lib/vm/useSettingsOrgStandardsVM';
import { formatRelativeTime } from '../lib/relativeTime';
import './SettingsOrgStandards.css';

const MANAGE_NOTE =
  'Save persists the standards and bumps the propagation timestamp. Archive removes the org-standards layer from every member’s agent until restored. Use the History tab to diff against a prior version and restore it.';
const READ_ONLY_NOTE =
  'You can read the standards so you understand what your agent will see. Owners and Admins author them.';
const READ_ONLY_EMPTY_NOTE =
  'No org standards have been authored yet.';
const SOFT_WARN_NOTE =
  'Long standards eat agent context window — consider splitting role-specific guidance into role rules or per-status instructions.';
const STARTER_NOTE =
  'Welcome — write your first standard. Start from one of these examples, or write your own:';
const PREVIEW_CAPTION =
  "This is Layer 1 — the org-wide standards every member's agent receives, ahead of per-role rules and per-status instructions. It reflects the unsaved Edit-tab working copy live.";
const PREVIEW_EMPTY_NOTE =
  'Nothing to preview yet — write some standards in the Edit tab.';
const PREVIEW_ARCHIVED_NOTE =
  'These standards are archived. Agents receive no org-standards layer until they are restored.';
const HISTORY_EMPTY_NOTE =
  'No prior versions yet. Save the standards to create the first snapshot.';

type StandardsTab = 'edit' | 'preview' | 'history';

const TAB_ITEMS = [
  { value: 'edit', label: 'Edit' },
  { value: 'preview', label: 'Preview' },
  { value: 'history', label: 'History' }
];

/**
 * `/settings/org-standards`.
 *
 * Single-pane tabbed editor — there is exactly one `org_standards` row per org,
 * so there is no left list. The Edit tab ships a monospace working-copy
 * textarea (Owner/Admin) or a rendered-markdown panel (Members). Preview is a
 * live read-only render of the working copy; History is a placeholder shell
 * that fills in at 10.2e.
 */
export function SettingsOrgStandards() {
  const vm = useSettingsOrgStandardsVM();

  return (
    <section
      className="set-os-page"
      data-testid="settings-org-standards"
      data-status={vm.status}
      data-viewer-role={vm.viewerRole ?? ''}
    >
      <SettingsBackLink />
      {vm.status === 'loading' ? (
        <div className="set-os-loading" data-testid="settings-os-loading">
          <Skeleton height={48} />
          <Skeleton height={360} />
        </div>
      ) : null}

      {vm.status === 'error' ? (
        <Banner kind="error" data-testid="settings-os-error">
          {vm.error ?? "Couldn't load org standards. Try again."}
        </Banner>
      ) : null}

      {vm.status === 'unauth' ? (
        <Banner kind="default" data-testid="settings-os-unauth">
          You need to sign in to view org standards.
        </Banner>
      ) : null}

      {vm.status === 'empty' ? (
        <Banner kind="default" data-testid="settings-os-no-org">
          You don't belong to any org yet.
        </Banner>
      ) : null}

      {vm.status === 'ready' && vm.standard ? (
        <ReadyView vm={vm} standard={vm.standard} />
      ) : null}

      {vm.saveToast ? (
        <Toast onDismiss={vm.actions.clearSaveToast}>{vm.saveToast}</Toast>
      ) : null}
      {vm.restoreToast ? (
        <Toast onDismiss={vm.actions.clearRestoreToast}>
          {vm.restoreToast}
        </Toast>
      ) : null}
    </section>
  );
}

function ReadyView({
  vm,
  standard
}: {
  vm: ReturnType<typeof useSettingsOrgStandardsVM>;
  standard: OrgStandard;
}) {
  const [tab, setTab] = useState<StandardsTab>('edit');
  const status = resolveStandardStatus(standard);

  return (
    <>
      <header className="set-os-head">
        <h1 className="set-os-title">Org standards</h1>
        <p className="set-os-lede">
          Org-wide "how we work here" prose, layered above per-role rules and
          per-status instructions when your agent reads any document. Set once;
          every member's agent sees it via MCP.
        </p>
        <div className="set-os-head-meta">
          <Tag data-testid="settings-os-status-chip">
            {standardStatusLabel(status)}
          </Tag>
          <MonoLabel
            as="span"
            size="sm"
            tone="muted"
            className="set-os-last-prop"
            data-testid="settings-os-last-propagated"
          >
            {`Last propagated: ${
              standard.lastPropagatedAt
                ? formatRelativeTime(standard.lastPropagatedAt)
                : '—'
            }`}
          </MonoLabel>
          {vm.canManage && status === 'archived' ? (
            <Button
              variant="primary"
              onClick={() => void vm.actions.restore()}
              disabled={vm.archiving}
              className="set-os-header-restore"
              data-testid="settings-os-restore"
            >
              {vm.archiving ? 'Restoring…' : 'Restore'}
            </Button>
          ) : null}
        </div>
      </header>

      <div className="set-os-editor" data-testid="settings-os-editor">
        <Tabs
          items={TAB_ITEMS}
          value={tab}
          onChange={(v) => setTab(v as StandardsTab)}
          label="Org standards editor tabs"
          dataTestId="settings-os-tabs"
        />

        {tab === 'edit' ? (
          <EditTab vm={vm} standard={standard} status={status} />
        ) : tab === 'preview' ? (
          <PreviewTab vm={vm} status={status} />
        ) : (
          <HistoryTab vm={vm} standard={standard} />
        )}
      </div>
    </>
  );
}

type HistoryModal =
  | { kind: 'view' | 'diff' | 'restore'; version: OrgStandardVersion }
  | null;

function HistoryTab({
  vm,
  standard
}: {
  vm: ReturnType<typeof useSettingsOrgStandardsVM>;
  standard: OrgStandard;
}) {
  const [modal, setModal] = useState<HistoryModal>(null);
  const versions = vm.selectedVersions;

  // Resolve the "(restored from <ts>)" subtext — the source snapshot's
  // created_at, looked up by restored_from_version_id within this list.
  const restoredFromLabel = (v: OrgStandardVersion): string | null => {
    if (!v.restoredFromVersionId) return null;
    const src = versions.find((x) => x.id === v.restoredFromVersionId);
    if (!src) return null;
    return `(restored from ${formatRelativeTime(src.createdAt)})`;
  };

  const authorHandle = (userId: string | null): string =>
    (userId && vm.handles[userId]) || 'someone';

  return (
    <div
      className="set-os-history"
      role="tabpanel"
      data-testid="settings-os-tab-history"
    >
      {versions.length === 0 ? (
        <p
          className="set-os-history-empty"
          data-testid="settings-os-history-empty"
        >
          {HISTORY_EMPTY_NOTE}
        </p>
      ) : (
        <table className="set-os-history-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">By</th>
              <th scope="col">Length</th>
              <th scope="col">
                <span className="set-os-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => {
              const restored = restoredFromLabel(v);
              return (
                <tr
                  key={v.id}
                  data-testid={`settings-os-history-row-${v.id}`}
                >
                  <td className="set-os-history-when">
                    <span>{formatRelativeTime(v.createdAt)}</span>
                    {restored ? (
                      <span className="set-os-history-restored-from">
                        {restored}
                      </span>
                    ) : null}
                  </td>
                  <td className="set-os-history-by">
                    {authorHandle(v.createdBy)}
                  </td>
                  <td className="set-os-history-len">
                    {`${v.content.length} chars`}
                  </td>
                  <td className="set-os-history-actions">
                    <Button
                      variant="ghost"
                      onClick={() => setModal({ kind: 'view', version: v })}
                      data-testid={`settings-os-history-view-${v.id}`}
                    >
                      View
                    </Button>
                    {vm.canManage ? (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setModal({ kind: 'diff', version: v })
                          }
                          data-testid={`settings-os-history-diff-${v.id}`}
                        >
                          Diff vs current
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setModal({ kind: 'restore', version: v })
                          }
                          disabled={vm.restoringVersionId !== null}
                          data-testid={`settings-os-history-restore-${v.id}`}
                        >
                          Restore
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {vm.restoreError ? (
        <Banner kind="error" data-testid="settings-os-restore-error">
          {vm.restoreError}
        </Banner>
      ) : null}

      <Modal
        open={modal?.kind === 'view'}
        onClose={() => setModal(null)}
        title={
          modal
            ? `Version from ${formatRelativeTime(
                modal.version.createdAt
              )} · by ${authorHandle(modal.version.createdBy)}`
            : 'Version'
        }
        dataTestId="settings-os-view-modal"
        footer={
          <Button
            variant="ghost"
            onClick={() => setModal(null)}
            data-testid="settings-os-view-close"
          >
            Close
          </Button>
        }
      >
        <pre
          className="set-os-view-version"
          data-testid="settings-os-view-content"
        >
          {modal?.version.content ?? ''}
        </pre>
      </Modal>

      <Modal
        open={modal?.kind === 'diff'}
        onClose={() => setModal(null)}
        title={
          modal
            ? `Version ${formatRelativeTime(
                modal.version.createdAt
              )} vs current`
            : 'Version diff'
        }
        dataTestId="settings-os-diff-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModal(null)}
              data-testid="settings-os-diff-close"
            >
              Close
            </Button>
            {vm.canManage && modal ? (
              <Button
                variant="primary"
                onClick={() =>
                  setModal({ kind: 'restore', version: modal.version })
                }
                data-testid="settings-os-diff-restore"
              >
                Restore this version
              </Button>
            ) : null}
          </>
        }
      >
        {modal ? (
          <DiffView
            before={modal.version.content}
            after={standard.content}
            beforeLabel="Prior version"
            afterLabel="Current"
            dataTestId="settings-os-diff-view"
          />
        ) : null}
      </Modal>

      <Modal
        open={modal?.kind === 'restore'}
        onClose={() => setModal(null)}
        title="Restore this version?"
        dataTestId="settings-os-restore-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModal(null)}
              data-testid="settings-os-restore-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (modal) void vm.actions.restoreVersion(modal.version.id);
                setModal(null);
              }}
              data-testid="settings-os-restore-confirm"
            >
              Restore
            </Button>
          </>
        }
      >
        The current content will be replaced with this version. A new snapshot
        of the current state is appended to history first, so the restore is
        reversible.
      </Modal>
    </div>
  );
}

function PreviewTab({
  vm,
  status
}: {
  vm: ReturnType<typeof useSettingsOrgStandardsVM>;
  status: ReturnType<typeof resolveStandardStatus>;
}) {
  // Live read-only render of the org-standards working copy. Org standards is
  // the top layer (Layer 1) itself, so there is no role-rule / per-status
  // layering to merge here — just this layer's prose.
  const archived = status === 'archived';
  const showRender = !archived && vm.editorValue.trim() !== '';

  return (
    <div
      className="set-os-preview"
      role="tabpanel"
      data-testid="settings-os-tab-preview"
    >
      <p
        className="set-os-layering-note"
        data-testid="settings-os-layering-note"
      >
        {PREVIEW_CAPTION}
      </p>
      {showRender ? (
        <Markdown className="set-os-merged" dataTestId="settings-os-merged">
          {vm.editorValue}
        </Markdown>
      ) : (
        <p
          className="set-os-preview-empty"
          data-testid="settings-os-preview-empty"
        >
          {archived ? PREVIEW_ARCHIVED_NOTE : PREVIEW_EMPTY_NOTE}
        </p>
      )}
    </div>
  );
}

function EditTab({
  vm,
  standard,
  status
}: {
  vm: ReturnType<typeof useSettingsOrgStandardsVM>;
  standard: OrgStandard;
  status: ReturnType<typeof resolveStandardStatus>;
}) {
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Members get a read-only rendered-markdown panel — no textarea, no controls.
  if (!vm.canManage) {
    const hasContent = standard.content.trim() !== '' && status !== 'archived';
    return (
      <div
        className="set-os-edit"
        role="tabpanel"
        data-testid="settings-os-tab-edit"
      >
        {hasContent ? (
          <Markdown
            className="set-os-readonly-render"
            dataTestId="settings-os-readonly-render"
          >
            {standard.content}
          </Markdown>
        ) : (
          <p
            className="set-os-readonly-empty"
            data-testid="settings-os-readonly-empty"
          >
            {READ_ONLY_EMPTY_NOTE}
          </p>
        )}
        <Banner kind="default" data-testid="settings-os-readonly-note">
          {READ_ONLY_NOTE}
        </Banner>
      </div>
    );
  }

  const archived = status === 'archived';
  const charCount = vm.editorValue.length;
  const overWarn = charCount > STANDARDS_SOFT_WARN;
  const showStarter = !archived && vm.editorValue.trim() === '';
  const canArchive = !archived && standard.id !== null;

  return (
    <div
      className="set-os-edit"
      role="tabpanel"
      data-testid="settings-os-tab-edit"
    >
      {showStarter ? (
        <div className="set-os-starter" data-testid="settings-os-starter">
          <p className="set-os-starter-text">{STARTER_NOTE}</p>
          <ul className="set-os-starter-list">
            {STARTER_EXAMPLES.map((example, i) => (
              <li key={i} className="set-os-starter-item">
                <Button
                  variant="ghost"
                  onClick={() => vm.actions.insertExample(example)}
                  data-testid={`settings-os-starter-example-${i}`}
                >
                  {example}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <TextAreaField
        id="settings-os-content"
        label="Standards content"
        className="set-os-textarea-field"
        value={vm.editorValue}
        onChange={(e) => vm.actions.editDraft(e.target.value)}
        rows={26}
        disabled={archived}
        placeholder="Write the org's standards…"
        data-testid="settings-os-textarea"
      />
      <MonoLabel
        as="div"
        size="sm"
        tone={overWarn ? 'fg' : 'muted'}
        className="set-os-char-counter"
        data-testid="settings-os-char-counter"
        data-warn={overWarn ? 'true' : 'false'}
      >
        {`${charCount} chars${overWarn ? ` · ${SOFT_WARN_NOTE}` : ''}`}
      </MonoLabel>

      {archived ? (
        <Banner kind="default" data-testid="settings-os-archived-note">
          These standards are archived. Every member's agent receives no
          org-standards layer until they are restored. Use Restore in the
          header to reactivate them.
        </Banner>
      ) : null}

      {vm.saveError ? (
        <Banner kind="error" data-testid="settings-os-save-error">
          {vm.saveError}
        </Banner>
      ) : null}
      {vm.archiveError ? (
        <Banner kind="error" data-testid="settings-os-archive-error">
          {vm.archiveError}
        </Banner>
      ) : null}

      {!archived ? (
        <div
          className="set-os-editor-actions"
          data-testid="settings-os-editor-actions"
        >
          {canArchive ? (
            <Button
              variant="ghost"
              className="set-os-archive-btn"
              onClick={() => setArchiveOpen(true)}
              disabled={vm.archiving}
              data-testid="settings-os-archive"
            >
              Archive
            </Button>
          ) : null}
          <Button
            variant="ghost"
            onClick={vm.actions.cancelEdit}
            disabled={!vm.isDirty || vm.saving}
            data-testid="settings-os-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void vm.actions.save()}
            disabled={!vm.isDirty || vm.saving}
            data-testid="settings-os-save"
          >
            {vm.saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : null}

      <Banner kind="default" data-testid="settings-os-manage-note">
        {MANAGE_NOTE}
      </Banner>

      <Modal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title="Archive these standards?"
        dataTestId="settings-os-archive-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setArchiveOpen(false)}
              data-testid="settings-os-archive-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setArchiveOpen(false);
                void vm.actions.archive();
              }}
              data-testid="settings-os-archive-confirm"
            >
              Archive
            </Button>
          </>
        }
      >
        Every member's agent will receive no org-standards layer until the
        standards are restored.
      </Modal>
    </div>
  );
}
